import {
  DeviceContext,
  RuntimeContextProvider,
  UserContext,
  EventTrackingContext,
  Identities,
} from '@mmt/events-schema'
import {
  getLocaleSettings,
  getMarketingCloudVisitorID,
  getProfileType,
  getUserFromStorage,
  isBrowserEnv,
  readCookieValue,
  PROFILE_TYPE_MAP,
  isGI,
  filterValidValues,
  LoginInfo,
} from './utils'
import { getDeviceInfo } from './device-info-provider'
import { Org, OrgValue } from '../../constants'

const NOT_AVAILABLE = 'NA'

function getTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (e) {
    return NOT_AVAILABLE
  }
}

function assertBrowserEnv() {
  if (!isBrowserEnv()) {
    throw new Error('only browser environment not supported')
  }
}

export const runtimeContextProvider: RuntimeContextProvider = {
  getDeviceContext(): DeviceContext {
    assertBrowserEnv()
    const { os, osVersion, networkType, version, browser, platform, deviceId, model, userAgent, acquired_country } =
      getDeviceInfo(window)

    const timeZone = getTimeZone()

    const deviceContext = {
      app_ver: version,
      vendor_id: browser,
      device_id: deviceId,
      os,
      os_version: osVersion,
      network_type: networkType,
      platform,
      time_zone: timeZone,
      model,
      user_agent: userAgent,
      acquired_country,
    }
    return filterValidValues(deviceContext) as DeviceContext
  },
  getUserContext(org): UserContext {
    assertBrowserEnv()
    const auth = readCookieValue(document.cookie, 'mmt-auth') || readCookieValue(document.cookie, 'OAUTH-GOIBIBO')
    const giUuid = readCookieValue(document.cookie, 'gitokenid') || undefined
    const mmtUserDet = getUserFromStorage(org)
    const marketingCloudId = getMarketingCloudVisitorID()

    if (isGI(org)) {
      const giUserContext = {
        uuid: giUuid,
        profile_type: PROFILE_TYPE_MAP.b2c,
        is_logged_in: !!auth,
        marketing_cloud_id: marketingCloudId,
        login_channel: OrgValue.GI,
        login_type: 'OTP',
      }
      return filterValidValues(giUserContext) as UserContext
    } else {
      const userContext: Partial<UserContext> = {}
      const wallet = localStorage.getItem('walletInfo')
      if (wallet) {
        const walletData = JSON.parse(wallet)
        const loyaltyData = walletData?.loyaltyData || {}
        if (loyaltyData?.status) {
          userContext.loyalty_status = loyaltyData.status
        }
        if (loyaltyData?.program) {
          userContext.loyalty_type = loyaltyData.program
        }
        if (loyaltyData?.currentTier?.name) {
          userContext.loyalty_level = loyaltyData.currentTier.name
        }
        if (walletData?.multiCurrencyDetails?.totalWalletAmount) {
          userContext.wallet_bal = walletData.multiCurrencyDetails.totalWalletAmount
        }
      }

      const loginChannel = localStorage.getItem('channel')
      if (loginChannel) {
        userContext.login_channel = loginChannel
      }

      const loginType = localStorage.getItem('loginType')
      if (loginType) {
        userContext.login_type = loginType
      }

      const mmtUserContext = {
        uuid: mmtUserDet?.uuid,
        profile_type: getProfileType(),
        is_logged_in: !!auth,
        marketing_cloud_id: marketingCloudId,
        login_channel: userContext.login_channel,
        login_type: userContext.login_type,
        nationality: mmtUserDet?.nationality,
        loyalty_status: userContext.loyalty_status,
        loyalty_type: userContext.loyalty_type,
        loyalty_level: userContext.loyalty_level,
        wallet_bal: userContext.wallet_bal,
      }

      return filterValidValues(mmtUserContext) as UserContext
    }
  },

  getEventTrackingContext(): EventTrackingContext {
    assertBrowserEnv()

    const localeSettings = getLocaleSettings()
    const { region, currency, userCurrency, language } = localeSettings

    const eventContext = {
      template_id: 0,
      topic_name: NOT_AVAILABLE,
      env: NOT_AVAILABLE,
      timezone: NOT_AVAILABLE,
      session_id: NOT_AVAILABLE,
      funnel_source: NOT_AVAILABLE,
      session_visit_number: 0,
      region,
      user_preferred_currency: userCurrency,
      domain_country: region,
      domain_language: language,
      domain_currency: currency,
    }

    return filterValidValues(eventContext) as EventTrackingContext
  },

  getUserIdentities(org: Org): Identities | null {
    const userDet = getUserFromStorage(org)

    let phone_number_hash = null
    let email_address_hash = null
    if (org === OrgValue.GI && userDet) {
      phone_number_hash = userDet?.value?.mHash
      email_address_hash = userDet?.value?.eHash
    } else if (userDet?.identities) {
      phone_number_hash = userDet?.identities?.mHash
      email_address_hash = userDet?.identities?.eHash
    } else if (Array.isArray(userDet?.loginInfoList)) {
      const mobileEntry = userDet.loginInfoList.find((entry: LoginInfo) => entry.loginType === 'MOBILE' && entry.commId)
      const emailEntry = userDet.loginInfoList.find((entry: LoginInfo) => entry.loginType === 'EMAIL' && entry.commId)
      if (mobileEntry) phone_number_hash = mobileEntry.commId
      if (emailEntry) email_address_hash = emailEntry.commId
    }

    const identities: Identities = {}
    if (phone_number_hash) identities.phone_number_hash = phone_number_hash
    if (email_address_hash) identities.email_address_hash = email_address_hash

    return Object.keys(identities).length ? identities : null
  },
}
